import { useState } from "react";
import type { ReactNode } from "react";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { Menu } from "../../../components/ui/Menu";
import { useT } from "../../../i18n";
import { TIMER_CHOICES, fmtLeft, sortStudents } from "../lib/consoleView";
import type { ConsoleCmd, ConsoleMode, ConsoleRoom, ConsoleStudent } from "../types";

/** 모드 카드가 보는 모드(설명서 2.2 ④) — 'relay-machine' 은 카드를 누르면 구간판을 연다(저장 · 시작 때 서버가 모드를 바꾼다) */
export const RELAY_MODE = "relay-machine";

/**
 * FS-11 · 1 준비(화면 설계 설명서 2.2 · 2.3 · 보완 v0.1 2.1 · 2.2) — 오늘 놀이 방식을 고르고, 그 모드에 필요한 준비만 한다.
 *  - 모드 카드(로비 · 밀어내기 · 협동 운반 · 자유 놀이터 · 릴레이 기계): 누르면 바로 그 모드로(릴레이는 구간판만 연다).
 *    고른 카드(picked)가 아닌 카드는 늘 누를 수 있다 — 구간판 · 배치판을 연 채 다른 카드를 누르면 그 카드로 돌아온다(onMode 가 처리).
 *    지금 모드는 초록 테두리 · 차시 원리에 맞는 모드에 '추천'(표시만 — 고르기는 교사).
 *  - 타이머(끔 · 5~30분)는 모드와 한 줄. 밀어내기에서는 경기 한 판도 타이머 시간(2026-10-11 — 룸 서버가 정한다 · 끄면 1분).
 *  - 아래 준비판(children): 밀어내기 · 협동 = 짝 · 팀 편성판 · 자유 놀이터 = 배치판 · 릴레이 = 구간판 · 로비 = 준비할 것 없음 안내.
 *  - 연결 칩: 연결 n/전체 · 끊긴 학생 이름 · 아직 안 들어온 수. 큰 버튼 하나 — 일시정지면 '▶ 시작', 아니면 '진행 화면으로 →'.
 */
export function PrepPanel({
  room,
  modes,
  picked,
  students,
  busy,
  progressTo,
  onMode,
  onRelay,
  onCmd,
  onStart,
  children,
}: {
  room: ConsoleRoom;
  modes: ConsoleMode[];
  /** 고른 카드(지금 모드 · 릴레이 구간판을 열었으면 relay-machine) */
  picked: string;
  students: ConsoleStudent[];
  busy: string | null;
  progressTo: string;
  onMode: (id: string) => void;
  onRelay: () => void;
  onCmd: (key: string, c: ConsoleCmd) => void;
  onStart: () => void;
  children: ReactNode;
}) {
  const t = useT();
  const [timer, setTimer] = useState<number | null>(null);
  const prewarm = room.state === "prewarm";
  const online = students.filter((s) => s.connection === "online").length;
  const dropped = sortStudents(
    students.filter((s) => s.connection === "dropped"),
    "name",
  );
  const offline = students.filter((s) => s.connection === "offline").length;
  const timerLabel = room.timer
    ? timer
      ? t("teacher.room.timerMin", { n: timer / 60 })
      : fmtLeft(room.timer.endsInS)
    : t("teacher.room.timerOff");

  return (
    <section
      className="pf-pane pf-pane--hero pf-prep"
      aria-labelledby="prep-title"
      data-testid="prep"
    >
      <div className="pf-row">
        <h2 id="prep-title" className="pf-h3">
          {t("teacher.room.prep.title")}
        </h2>
        <span className="pf-rc__label pf-row__end" aria-hidden="true">
          {t("teacher.room.timerLabel")}
        </span>
        <Menu
          testId="prep-timer"
          ariaLabel={t("teacher.room.timerLabel")}
          disabled={prewarm || busy !== null}
          label={<>{timerLabel} ▾</>}
          items={TIMER_CHOICES.map((s) => ({
            id: String(s ?? "off"),
            on: s === null ? !room.timer : !!room.timer && timer === s,
            label:
              s === null ? t("teacher.room.timerOff") : t("teacher.room.timerMin", { n: s / 60 }),
          }))}
          onPick={(id) => {
            const s = id === "off" ? null : Number(id);
            setTimer(s);
            onCmd("timer", { kind: "timer", seconds: s });
          }}
        />
      </div>
      <ul className="pf-modecards" data-testid="mode-cards" role="list">
        {modes.map((m) => {
          const on = m.id === picked;
          return (
            <li key={m.id}>
              <button
                type="button"
                className={`pf-modecard${on ? " pf-modecard--on" : ""}`}
                aria-pressed={on}
                data-mode={m.id}
                disabled={prewarm || busy !== null}
                onClick={() =>
                  m.id === RELAY_MODE ? onRelay() : m.id !== picked ? onMode(m.id) : undefined
                }
              >
                <span className="pf-modecard__name">{t(`teacher.room.mode.${m.id}`)}</span>
                <span className="pf-modecard__desc">{t(`teacher.room.modeDesc.${m.id}`)}</span>
                {m.recommended && (
                  <span className="pf-chip-static pf-chip-static--ok pf-modecard__rec">
                    {t("teacher.room.recommended")}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="pf-prep__board" data-testid="prep-board" data-mode={picked}>
        {children}
      </div>
      <div className="pf-prep__foot">
        <div className="pf-row pf-row--wrap" data-testid="prep-conn">
          <span className="pf-chip-static pf-chip-static--ok">
            {t("teacher.room.prep.online", { n: online, total: students.length })}
          </span>
          {dropped.map((s) => (
            <span key={s.pid} className="pf-chip-static pf-chip-static--err" data-pid={s.pid}>
              {s.nickname} ·{" "}
              {s.droppedS != null
                ? t("teacher.room.conn.dropped", { s: s.droppedS })
                : t("teacher.room.conn.droppedNoS")}
            </span>
          ))}
          {offline > 0 && (
            <span className="pf-chip-static pf-chip-static--warn">
              {t("teacher.room.prep.offline", { n: offline })}
            </span>
          )}
        </div>
        {prewarm ? (
          <Button variant="green" className="pf-prep__go" disabled data-testid="prep-go">
            {t("teacher.room.prep.prewarm")}
          </Button>
        ) : room.state === "paused" ? (
          <Button
            variant="green"
            className="pf-prep__go"
            disabled={busy !== null}
            data-testid="prep-go"
            onClick={onStart}
          >
            {t("teacher.room.start")}
          </Button>
        ) : (
          <ButtonLink to={progressTo} variant="green" className="pf-prep__go" data-testid="prep-go">
            {t("teacher.room.prep.goProgress")}
          </ButtonLink>
        )}
      </div>
    </section>
  );
}

/** 로비(고른 모드에 준비할 것이 없을 때 — 설명서 2.2 ⑦) */
export function PrepNothing() {
  const t = useT();
  return (
    <div className="pf-banner pf-banner--info" role="note" data-testid="prep-nothing">
      <div className="pf-banner__text">{t("teacher.room.prep.nothing")}</div>
    </div>
  );
}
